/* THE MASS OVER THE GRID.
 *
 * A wall of trees was 16×16 stamps in a row, four variants deep, and it read
 * exactly like what it was: an examiner measured 21 unique renderings across
 * 653 instances, 66% of solid tiles sitting in a straight run of four or more,
 * and the bottom row of every heath screen an unbroken line of identical
 * trees. Adding more variants does not fix that, because the fault is not the
 * variety of the stamp — it is that the silhouette of a forest is on the tile
 * grid at all. Real canopy is a few big shapes that happen to cover a region.
 *
 * So the tiles keep deciding what is SOLID — collision, pathing and gating all
 * still read the grid, and nothing here can move a wall — and this decides
 * what is DRAWN over the region they describe:
 *
 *   1. every solid tile is filled with the mass's under-colour, so nothing
 *      can show through no matter where the shapes land;
 *   2. crowns are placed by blue noise across the region, at positions with
 *      no relation to the 16px lattice, sized by how deep in the mass they
 *      sit — big in the middle of a wood, small at its edge;
 *   3. they spill OUTWARD past the region's boundary, which is what breaks
 *      the straight line: the silhouette is the union of a dozen circles, not
 *      the outline of a set of squares.
 *
 * A TREE IS BIGGER THAN THE PLAYER. Crowns used to run 14-38px across against
 * a 13px body, which put the smaller ones at about the player's own size — and
 * because `step` was small enough that neighbours overlapped heavily, a wood
 * merged into one flat green border rather than reading as trees at all.
 * Reported from play as "the trees are the same size as the player".
 *
 * Crown radii and spacing were raised together, roughly 1.5x. Raising size
 * alone makes it worse: bigger crowns at the old spacing simply merge more
 * completely, so the wood gets flatter, not grander.
 *
 * Each biome gets its own geometry rather than a recolour, because a boulder
 * field and a wood are not the same shape: `SHAPES` below.
 *
 * All of it is baked once per screen into one canvas. The cost is a few
 * hundred spans at load, and nothing per frame.
 */
import { smoothLabelsSteps } from './groundfield.ts';
import { T, BIOMES } from './art.ts';
import { paintCrownBody, paintCrownLit, paintTrunk } from './flora.ts';
import { distanceField } from './light.ts';
import { atlasCell, onAtlasReset } from './atlas.ts';
import { mixHex, windAt, screenDrift, drifted } from './grass.ts';

const TS = 16;

/** the tile each biome's mass is made of, and how it is drawn */
export const SHAPES = {
  // A WOOD. Round, lumpy crowns that overlap into one canopy, with trunks
  // showing only where the mass ends and you could see under it.
  heath: { of: T.TREE, kind: 'broadleaf', min: 11, max: 22, step: 15, lumpy: 0.28, trunk: 3 },
  bloom: { of: T.TREE, kind: 'broadleaf', min: 11, max: 23, step: 15, lumpy: 0.32, trunk: 3 },
  // A FEN. Lower, wider, wetter — crowns flatten into thickets.
  fen:   { of: T.TREE, kind: 'broadleaf', min: 10, max: 20, step: 14, lumpy: 0.38, trunk: 0, squash: 0.78 },
  // SCRUB. Sparse, narrow crowns on tall bare trunks, so you see the ground
  // between them and the wall reads as scattered rather than solid.
  dust:  { of: T.TREE, kind: 'scrub', min: 9, max: 17, step: 13, lumpy: 0.26, trunk: 7, squash: 0.86 },
  // A BOULDER FIELD. Not foliage at all: hard, flat-topped, stacked lumps.
  scree: { of: T.ROCK, kind: 'boulder', min: 7, max: 14, step: 10, lumpy: 0.26, trunk: 0, squash: 0.72 },
  // BURNT SPIRES. Thin, tall, ragged — the shape of a wood after a fire.
  ash:   { of: T.TREE, kind: 'snag', min: 8, max: 15, step: 12, lumpy: 0.5, trunk: 9, squash: 1.25 },
};

/** ...and the second mass every biome has: the rock that is not its feature.
 *  Without this, a scree wall gets boulders and the three rocks sitting in a
 *  heath meadow are still 16px stamps in a row. */
const ROCKS = { of: T.ROCK, kind: 'boulder', min: 5, max: 9, step: 7, lumpy: 0.28, trunk: 0, squash: 0.7 };

/** Which masses move. Foliage catches the wind; stone and dead wood do not,
 *  and telling them apart is what `spec.kind` is for. */
const MOVES = new Set(['broadleaf', 'scrub']);

/** deterministic 0..1 from a seed and two integers */
const px = (seed, x, y) => {
  let h = (seed ^ (x * 374761393) ^ (y * 668265263)) >>> 0;
  h = Math.imul(h ^ (h >>> 13), 1274126177) >>> 0;
  h = Math.imul(h ^ (h >>> 16), 2246822519) >>> 0;
  return ((h ^ (h >>> 15)) >>> 8) / 16777216;
};

/** How deep inside the mass each tile is, in tiles, by repeated erosion. A
 *  crown in the middle of a wood is bigger than one on its edge — which is
 *  what makes the edge ragged and the middle solid. */
function depthMap(tiles, cols, rows, isMass) {
  const d = [];
  for (let y = 0; y < rows; y++) {
    const r = [];
    for (let x = 0; x < cols; x++) r.push(isMass(tiles[y][x]) ? 1 : 0);
    d.push(r);
  }
  for (let pass = 1; pass < 5; pass++) {
    const next = d.map((r) => r.slice());
    for (let y = 0; y < rows; y++) {
      for (let x = 0; x < cols; x++) {
        if (d[y][x] !== pass) continue;
        // OFF THE EDGE OF THE SCREEN IS NOT THE EDGE OF THE WOOD. Treating
        // out-of-bounds as open eroded the whole border ring every pass, so
        // the outer wall — which is the wall you always see — got none of the
        // big crowns and stayed a flat line.
        const n = [[1, 0], [-1, 0], [0, 1], [0, -1]].every(([dx, dy]) => {
          const yy = y + dy, xx = x + dx;
          if (yy < 0 || yy >= rows || xx < 0 || xx >= cols) return true;
          return d[yy][xx] >= pass;
        });
        if (n) next[y][x] = pass + 1;
      }
    }
    for (let y = 0; y < rows; y++) for (let x = 0; x < cols; x++) d[y][x] = next[y][x];
  }
  return d;
}

/** The plan for one mass, without a canvas: which tiles it covers and where
 *  the crowns go. Exported so the shape of the thing can be checked headless
 *  — `bakeCanopy` needs a 2D context and there is none in node, and a check
 *  that reads the source text instead is not a check. */
export function canopyPlan(tiles, biome, cols, rows, seed, ox = 0, oy = 0, pad = 0, biomeAt: any = null) {
  const specs = specsFor(tiles, biome, cols, rows, ox, oy, pad, biomeAt);
  const out = [];
  /* A TREE TAKES THE SHAPE OF ITS OWN BIOME, NOT ITS SCREEN'S. One shape per
   * screen drew a wood that straddled a heath/ash boundary as broadleaf on one
   * screen and dead snags on the next -- 36% of the crowns reaching over a
   * seam were a different crown from the other side. With `biomeAt`, the tree
   * tiles are split by the biome under each, one layer per shape, each planned
   * on a grid where the other shapes' trees are ground. */
  const treeShapeAt = (x, y) => {
    const b = biomeAt ? biomeAt(ox * cols + x - pad, oy * rows + y - pad) : biome;
    const cand = SHAPES[b] || SHAPES.heath;
    return cand.of === T.TREE ? cand : SHAPES.heath;
  };
  const treeSpecs = new Map();
  if (biomeAt) {
    for (let y = 0; y < tiles.length; y++) for (let x = 0; x < tiles[y].length; x++) {
      if (tiles[y][x] !== T.TREE) continue;
      const sp = treeShapeAt(x, y);
      if (!treeSpecs.has(sp)) treeSpecs.set(sp, sp);   // by identity: three biomes share the name broadleaf and differ in step
    }
  }
  const layers = biomeAt
    ? specs.filter((sp) => sp.of !== T.TREE).concat([...treeSpecs.values()])
    : specs;
  for (const spec of layers) {
    const grid = (biomeAt && spec.of === T.TREE)
      ? tiles.map((row, y) => row.map((v, x) => (v === T.TREE && treeShapeAt(x, y) !== spec) ? T.GROUND : v))
      : tiles;
    /* THE MASS IS THE SCREEN'S OWN TILES. `grid` is the padded grid when a
     * pad is given, and this read its top-left corner as the screen: every
     * trunk and shadow stood `pad` tiles up and left of its tree. At a pad
     * of two that was a small slip; at six it was a wood's shadow lying on
     * open ground a third of a screen away. */
    const mass = [];
    for (let y = 0; y < rows; y++) {
      for (let x = 0; x < cols; x++) if (grid[y + pad][x + pad] === spec.of) mass.push([x, y]);
    }
    if (!mass.length) continue;
    const crowns = crownsFor(grid, cols + pad * 2, rows + pad * 2, seed, spec,
      ox * cols * TS - pad * TS, oy * rows * TS - pad * TS);
    out.push({ spec, mass, crowns, pad });
  }
  return out;
}

/** WHICH MASSES THIS SCREEN HAS TO DRAW — asked in ONE place.
 *
 * `canopyPlan` and `bakeCanopy` each had their own copy of these three lines,
 * and they drifted the moment one of them was fixed: the geometry the seam
 * checks read got both masses while the bake the player actually sees kept
 * only one, so the fix measured green and changed nothing on screen. This file
 * already carries a note about six independent models of one question being
 * where every softlock came from; this is the same shape of mistake in the
 * art. There is one now, and both callers use it.
 *
 * BOTH MASSES, ALWAYS -- THE COUNTRY IS PER TILE NOW.
 *
 * This took the screen's own biome, added the rock mass if that biome's
 * feature was not already rock, and stopped. It was right while a screen had
 * one country: `scree`'s mass IS rock, so it needed nothing more.
 *
 * The countries are a terrain field now and run through screens, so a screen
 * keyed `scree` is routinely full of the next country's trees -- and with no
 * TREE spec in the list not one of them got a crown. What shows instead is the
 * bare `T.TREE` tile sprite: one identical stamp per tile, dead centre, locked
 * to the 16px grid. A wood came out as an orchard, in rows and columns, which
 * is precisely the lattice this layer exists to destroy, and screens either
 * side of such a boundary looked like different games.
 *
 * The tree shape is taken from the country the trees actually stand in rather
 * than from the screen's key, because a fen's thicket and a dust scrub are
 * different plants and the tile they grow from does not say which.
 */
function specsFor(tiles, biome, cols, rows, ox, oy, pad, biomeAt) {
  const own = SHAPES[biome] || SHAPES.heath;
  const specs = [own];
  if (own.of !== T.ROCK) specs.push(ROCKS);
  else {
    let treeSpec = null;
    for (let y = 0; y < tiles.length && !treeSpec; y++) {
      for (let x = 0; x < tiles[y].length && !treeSpec; x++) {
        if (tiles[y][x] !== T.TREE) continue;
        const b = biomeAt ? biomeAt(ox * cols + x - pad, oy * rows + y - pad) : null;
        const cand = (b && SHAPES[b]) || SHAPES.heath;
        treeSpec = cand.of === T.TREE ? cand : SHAPES.heath;
      }
    }
    if (treeSpec) specs.push(treeSpec);
  }
  return specs;
}

/** Crowns for one mass, placed by a jittered grid — blue noise without the
 *  dart-throwing, which matters because this runs on every screen load. */
function crownsFor(tiles, cols, rows, seed, spec, opx, opy) {
  const isMass = (t) => t === spec.of;
  /* THE BAKE IS WIDER THAN THE SCREEN, and that is what unclips the wood.
   *
   * The canvas used to be exactly the playfield, so every crown within a
   * radius of an edge had to be SHRUNK to fit it — and the screen next door,
   * looking at the same world position from the other side, shrank it by a
   * different amount or dropped it entirely because it could only see its own
   * tiles. The result was a band of undersized crowns along every seam, which
   * measured as the whole of the remaining lattice: with the canopy pass
   * disabled the seam statistic fell from 6.4-17.3 luma to -11.8 to -3.2,
   * indistinguishable from mid-screen.
   *
   * With `pad` tiles of margin the grid extends past the playfield on every
   * side, the tiles out there are the REAL neighbouring tiles, and a crown is
   * drawn whole at its true size by whichever screen owns it. */
  const depth = depthMap(tiles, cols, rows, isMass);
  const out = [];
  const step = spec.step;
  const W = cols * TS, H = rows * TS;
  /* THE LATTICE IS A WORLD LATTICE, NOT A PER-SCREEN ONE.
   *
   * The jitter, radius, variant and tint are all hashed from world
   * coordinates — which was meant to make a crown the same crown from either
   * side of a seam, and did not, because the GRID those coordinates were
   * sampled on restarted at each screen. `step` is 7-12px and a screen is
   * 256x192, so the lattice only lined up across a seam when `step` happened
   * to divide the screen; measured, 59 of 5,881 crowns that overhang a seam
   * were drawn by both sides. The other 5,822 were two different trees in one
   * silhouette.
   *
   * Anchoring the grid to a multiple of `step` in WORLD space costs two lines
   * and makes `wx`/`wy` below land on the same lattice points no matter which
   * screen is asking. */
  const gx0 = -(((opx % step) + step) % step) - step;
  const gy0 = -(((opy % step) + step) % step) - step;
  for (let gy = gy0; gy < H + step; gy += step) {
    for (let gx = gx0; gx < W + step; gx += step) {
      // world coordinates, so a crown does not jump when you walk back onto
      // the screen it is on
      const wx = gx + opx, wy = gy + opy;
      const jx = gx + Math.floor((px(seed + 11, wx, wy) - 0.5) * step * 1.3);
      const jy = gy + Math.floor((px(seed + 29, wx, wy) - 0.5) * step * 1.3);
      const tx = Math.floor(jx / TS), ty = Math.floor(jy / TS);
      /* A CROWN IS ALLOWED TO STAND JUST OFF THE SCREEN AND LEAN IN.
       *
       * This used to `continue` the moment a jittered centre left the
       * playfield, and that is what made every screen seam a DOUBLE HEDGE.
       * Measured over 12 worlds: 87.5% of border tile pairs are solid on both
       * sides — screen A's forced border wall back to back with screen B's —
       * and because neither screen would grow a crown centred in the other,
       * each one capped its wood at its own edge. Two ranks of trees meeting
       * on a ruled line, at every one of the world's 112 seams.
       *
       * The loop already sweeps from `-step` to `H + step`, so the candidates
       * were always being generated; only this test threw them away. Letting
       * a centre sit one tile outside and reading the depth of the edge tile
       * it leans on fixes the join for free — because `wx`/`wy` are WORLD
       * coordinates, screen A and screen B compute the identical jitter,
       * radius, variant and tint for the same world position, so both draw
       * the same crown and it lands continuous across the seam.
       *
       * `!dp` still guards it: the edge tile has to actually be mass. At a
       * doorway, where the border is cut open, nothing is invented over the
       * gap. */
      const ex = Math.min(cols - 1, Math.max(0, tx));
      const ey = Math.min(rows - 1, Math.max(0, ty));
      if (Math.abs(tx - ex) > 1 || Math.abs(ty - ey) > 1) continue;
      if (!depth[ey][ex]) continue;
      /* AT THE RIM, BOTH SIDES MUST AGREE ON THE DEPTH.
       *
       * `depth` is depth into THIS screen's mass, so the screen that contains
       * a crown's centre measures it properly while its neighbour, looking at
       * the same world position from outside, measures its own edge tile.
       * Different depths mean different radii mean two different trees — and
       * that alone left 2,175 of 5,692 straddling crowns disagreeing after
       * the lattice was fixed.
       *
       * A crown centred on the border ring, or just outside it, is at the rim
       * of the wood BY DEFINITION and from either side. Calling that depth 1
       * is both true and the only value the two screens can both compute. */
      const rim = tx <= 0 || ty <= 0 || tx >= cols - 1 || ty >= rows - 1;
      const dp = rim ? 1 : depth[ey][ex];
      // deep in the mass -> big; on its edge -> small. This is the whole
      // reason the silhouette stops being a rectangle.
      const t = Math.min(1, (dp - 1) / 2.2);
      const r = Math.round(spec.min + (spec.max - spec.min) * (0.35 + 0.65 * t)
        * (0.75 + 0.5 * px(seed + 53, wx, wy)));
      // A CROWN THAT DOES NOT FIT IS LOWERED, NOT SLICED.
      //
      // The bake is exactly the playfield, so a crown centred near row 0
      // reached above y=0 and was cut by the canvas edge — every screen's top
      // row of trees ended in a straight horizontal line while its other three
      // edges were ragged. Reported from play as the tops of the trees being
      // cut off, and made much more visible by enlarging the crowns.
      //
      // Moving the centre down keeps the tree its proper size and costs only a
      // little coverage at the very top, which the mass fill underneath is
      // already holding. Shrinking it instead would make every treeline along
      // a screen edge a row of saplings.
      // SHRINK TO FIT, DO NOT LINE UP.
      //
      // The first attempt at this moved a crown's CENTRE down until it fitted
      // — which is worse, and was reported again: every crown that would have
      // overhung ends up at exactly the same y, so a screen whose top row is
      // all trees gets a rank of crowns with their tops on one ruled line. It
      // traded "cut off" for "all cut to the same height", which looks the
      // same and is the same complaint.
      //
      // Reducing the radius keeps each crown at its own height, so the tops
      // stay uneven, and the sizes were already varying with depth into the
      // mass. Only when a crown would have to become a shrub is it lowered
      // instead, which is rare and does not line anything up.
      let rr = Math.max(3, r);
      let yy = jy;
      const sq = spec.squash || 1;
      if (yy - rr * sq < 0) {
        const fit = Math.floor(yy / sq);
        if (fit >= 5) rr = Math.min(rr, fit);
        else { rr = Math.min(rr, 5); yy = Math.max(yy, Math.ceil(rr * sq)); }
      }
      if (yy + rr * sq > H) {
        const fit = Math.floor((H - yy) / sq);
        if (fit >= 5) rr = Math.min(rr, fit);
        else { rr = Math.min(rr, 5); yy = Math.min(yy, H - Math.ceil(rr * sq)); }
      }
      out.push({ x: jx, y: yy, r: rr, depth: dp,
        h: px(seed + 71, wx, wy), lift: Math.floor(px(seed + 97, wx, wy) * 3),
        // the crown's own shape variant, from its WORLD position so a rock
        // keeps its shape when you walk back onto the screen it is on
        v: px(seed + 53, wx, wy),
        /* AND ITS OWN COLOUR. A per-screen drift makes one wood differ from
         * the next wood, which you can only see by walking between them. What
         * makes a wood look like TREES is neighbouring crowns being different
         * greens — so each one carries its own -1..+1 tint, read from the same
         * world position as its shape, and stable for the same reason. */
        tint: px(seed + 131, wx, wy) * 2 - 1 });
    }
  }
  // EVERY TILE OF THE MASS GETS A SHAPE. The crowns are placed on a jittered
  // grid of `step` 7-12px, so an isolated tile — one rock in a meadow, the
  // corner of a wood — could receive none, and `paint()` fills every mass
  // tile with the shadow tone first: the result is a perfectly flat 16x16
  // black square sitting in bright grass, which is the tile grid coming back
  // as the most conspicuous artefact the layer can produce. Measured: 31 of
  // 14,064 tiles over 3 seeds, but up to FOUR on one screen.
  // a tile counts as covered when its CENTRE lies within 0.7r of a crown --
  // stricter than sampling the crown's box every half tile, which could mark
  // a tile whose centre the crown never reached (one bare tile in 1972)
  const covered = new Set();
  for (const c of out) {
    const r = Math.max(1, Math.round(c.r * 0.7));
    const tx0 = Math.floor((c.x - r) / TS), tx1 = Math.floor((c.x + r) / TS);
    const ty0 = Math.floor((c.y - r) / TS), ty1 = Math.floor((c.y + r) / TS);
    for (let ty = ty0; ty <= ty1; ty++) for (let tx = tx0; tx <= tx1; tx++) {
      if (Math.abs(c.x - (tx * TS + TS / 2)) <= r && Math.abs(c.y - (ty * TS + TS / 2)) <= r) covered.add(tx + ',' + ty);
    }
  }
  for (let ty = 0; ty < rows; ty++) {
    for (let tx = 0; tx < cols; tx++) {
      if (!isMass(tiles[ty][tx]) || covered.has(tx + ',' + ty)) continue;
      const wx = tx * TS + opx, wy = ty * TS + opy;
      /* JITTERED AND SIZED LIKE ANY OTHER CROWN, AND THAT IS THE WHOLE POINT.
       *
       * This wrote the crown to the exact centre of the tile at one fixed
       * radius. Written for the isolated tile the note above describes -- 31 of
       * 14,064 -- it is fine at that rate and catastrophic above it: dust's
       * scrub is placed on a 13px grid against 16px tiles, so a dense wood
       * drops a large share of its tiles through to here, and every one of them
       * got the SAME crown at the SAME offset. A wood came out as an orchard --
       * identical stamps in rows and columns, locked to the tile grid, which is
       * the grid artefact this whole layer exists to destroy.
       *
       * The jitter and the radius are taken the same way the placed crowns take
       * theirs, off the WORLD position, so a fallen-through tile is
       * indistinguishable from a placed one and still lands identically from
       * either side of a seam. */
      const rf = Math.max(4, Math.round(spec.min * (0.66 + 0.42 * px(seed + 149, wx, wy))));
      // ...but never so far that the crown's own tile slips out from under its
      // 0.7r body: one tile in 1972 baked as a bare block that way
      const band = Math.max(0, TS / 2 - rf * 0.7 - 1);
      const jx = Math.round((px(seed + 131, wx, wy) - 0.5) * 2 * band);
      const jy = Math.round((px(seed + 137, wx, wy) - 0.5) * 2 * band);
      out.push({ x: tx * TS + TS / 2 + jx, y: ty * TS + TS / 2 + jy,
        r: rf,
        depth: depth[ty][tx],
        h: px(seed + 71, wx, wy), lift: Math.floor(px(seed + 97, wx, wy) * 3),
        // the crown's own shape variant, from its WORLD position so a rock
        // keeps its shape when you walk back onto the screen it is on
        v: px(seed + 53, wx, wy) });
    }
  }
  // A CROWN NEVER GROWS OVER A WAY IN.
  //
  // Crowns are deliberately off-grid and up to 18px across, wider than the
  // 16px tile they belong to, and `drawCanopy` paints AFTER `drawTiles` — so
  // a boulder or a tree standing next to a cave entrance covered it. Reported
  // from play as "the rock texture is covering entrances". Measured over 40
  // worlds: 19 of 197 entrances (9.6%) had a crown across the tile centre,
  // every one of them a HOLE — secret caves sit in rocky ground, which is
  // exactly where rock crowns grow, while hollow mouths sit on cleared ground
  // and were never hit.
  //
  // The radius is clamped and the crown is NEVER dropped. Dropping one leaves
  // the flat 16x16 shadow square the fallback pass above exists to prevent,
  // which is a worse artefact than the thing being fixed — so a crown that
  // would have to vanish is held at MIN instead. A crown may lean GIVE px
  // into an entrance so the edge stays organic, and a held-at-MIN one may
  // nibble a few pixels of its corner; what neither may do is reach the
  // middle of the tile, which is what makes an entrance unreadable. Cost,
  // measured: 218 of 655,133 crowns clamped (0.03%), none dropped, and
  // entrance coverage goes from 19 of 197 to 0.
  const GIVE = 2, MIN = 3;
  const ways = [];
  for (let ty = 0; ty < rows; ty++) {
    for (let tx = 0; tx < cols; tx++) {
      const v = tiles[ty][tx];
      if (v === T.RAMP) {   // the one way in a screen still has (LS-226): a cut stair between terraces
        ways.push([tx * TS, ty * TS, tx * TS + TS, ty * TS + TS]);
      }
    }
  }
  for (const c of ways.length ? out : []) {
    for (const [x0, y0, x1, y1] of ways) {
      let dx = Math.max(x0 - c.x, 0, c.x - x1);
      let dy = Math.max(y0 - c.y, 0, c.y - y1);
      let d = Math.hypot(dx, dy) + GIVE;
      /* ...AND IT STEPS BACK BEFORE IT SHRINKS. A crown sitting on the edge
       * of an entrance's tile was held at MIN where it stood, and a lone
       * tree under a cave hole baked as a three-pixel crown on the hole's
       * rim with its own tile a bare block beneath (canopy-b 6,7 @4,3).
       * Pushed six px away from the entrance first -- down, up, right or
       * left, whichever side it is on -- it keeps a crown its tile can
       * stand under, and still never reaches the entrance's middle. */
      if (c.r > d && d < 7) {
        if (c.y >= y1) c.y = Math.max(c.y, y1 + 6); else if (c.y <= y0) c.y = Math.min(c.y, y0 - 6);
        else if (c.x >= x1) c.x = Math.max(c.x, x1 + 6); else if (c.x <= x0) c.x = Math.min(c.x, x0 - 6);
        dx = Math.max(x0 - c.x, 0, c.x - x1); dy = Math.max(y0 - c.y, 0, c.y - y1); d = Math.hypot(dx, dy) + GIVE;
      }
      if (c.r > d) c.r = Math.max(MIN, d);
    }
  }
  const clear = out;

  // paint back to front so lower crowns overlap higher ones, which is the
  // only depth cue a flat canopy has
  clear.sort((a, b) => a.y - b.y);
  return clear;
}

/** A crisp filled blob: one integer span per row, radius modulated per row so
 *  the outline is lumpy rather than a circle. `arc()` would antialias, and a
 *  soft edge in a 256×240 buffer is the one thing this art style cannot do. */
/* FOUR SHAPES, NOT FOUR SETS OF NUMBERS.
 *
 * `spec.kind` was declared on all seven masses and read by nothing — every
 * biome went through one ellipse-with-a-wobble and differed only in radius
 * and squash, which is why a scree boulder field and an ash burn rendered as
 * the same round blobs in a different tint. An examiner found it by grepping:
 * seven declarations, zero reads. The claim that each biome had its own
 * geometry was simply false, so here is the geometry.
 *
 * Each returns the half-width of the shape at row `dy` of `ry`, given the
 * per-row wobble. `t` runs 0 at the top of the shape to 1 at the bottom.
 */
export const PROFILE = {
  /* A CROWN. Widest above the middle, tapering to a rounded point: the shape
   * of a canopy seen from slightly above.
   *
   * TWO RADII, BECAUSE ONE ELLIPSE CANNOT CLOSE AT BOTH ENDS.
   *
   * This was a single ellipse centred at t=0.42 with a half-height of 0.58,
   * which reaches zero at t = -0.16 — ABOVE the range it is ever evaluated
   * over. So the first row of every crown was drawn at 69% of full width and
   * the shape simply began there: a flat lid on a round body, on every
   * broadleaf tree in the game, nowhere near a screen edge. Reported three
   * times as the trees being cut off at the top, and the first two things I
   * changed were the screen edge and the bake, neither of which was it.
   *
   * The half-height is now the distance to whichever end is nearer, so the
   * curve closes at t=0 AND t=1 while still being widest above the middle.
   */
  broadleaf: (t) => Math.sqrt(Math.max(0, 1 - ((t - 0.42) / (t < 0.42 ? 0.42 : 0.58)) ** 2)),
  // SCRUB. Thin and open — a few branches, not a mass. Narrower everywhere
  // and pinched hard at the base where the bare trunk shows. Same two-radii
  // fix: it was centred at 0.35 with a half-height of 0.65 and had the same
  // flat lid.
  scrub: (t) => Math.sqrt(Math.max(0, 1 - ((t - 0.35) / (t < 0.35 ? 0.35 : 0.65)) ** 2))
    * (0.55 + 0.45 * (1 - t)),
  // A BOULDER. Flat on the bottom, domed on top, and it does NOT taper to a
  // point — stone sits on the ground, it does not hang over it.
  // FOUR ROCKS, NOT ONE ROCK AT FOUR SIZES.
  //
  // This was a single curve, so every boulder in the game had the same
  // outline and differed only by radius and a +-16% per-row wobble — which is
  // exactly what it looked like: scaled clones of one rock. Reported from
  // play. Foliage got away with the same trick because `lumpy` is 0.28-0.5
  // for a crown and the eye reads a wood as a texture; a boulder field is
  // read as objects, and objects that are all the same object are noticed.
  //
  // `v` is the crown's own variant, stable per world position, so a given
  // rock keeps its shape when you walk back onto the screen.
  boulder: (t, v = 0) => {
    const k = Math.floor(v * 4) % 4;
    // a squat dome, wide at the foot
    if (k === 0) return t > 0.86 ? 0.94 : Math.sqrt(Math.max(0, 1 - ((t - 0.62) / 0.72) ** 2));
    // a slab: near-vertical sides and a flat top
    if (k === 1) return t < 0.22 ? 0.52 + t * 1.4 : (t > 0.90 ? 0.88 : 0.86 + 0.14 * Math.sin(t * 3.1));
    // a wedge: narrow at the top, spreading to the ground
    if (k === 2) return Math.min(1, 0.30 + t * 1.05);
    // a taller boulder with its shoulder high up
    return t > 0.78 ? 0.90 : Math.sqrt(Math.max(0, 1 - ((t - 0.40) / 0.86) ** 2));
  },
  // A SNAG. A burnt spire: wide at the foot, straight up to a broken tip.
  snag: (t) => 0.28 + 0.72 * t ** 1.6,
};

/** A crisp filled shape: one integer span per row, the half-width from the
 *  mass's own profile and modulated per row so the outline is ragged rather
 *  than ruled. `arc()` would antialias, and a soft edge in a 256x240 buffer
 *  is the one thing this art style cannot do. */
function blob(g, cx, cy, r, squash, lumpy, seed, kind = 'broadleaf', v = 0) {
  for (const [x, y, w] of blobSpans(cx, cy, r, squash, lumpy, seed, kind, v)) {
    g.fillRect(x, y, w, 1);
  }
}

/**
 * The rows a blob would fill, as [x, y, width].
 *
 * Split out of `blob` and exported for the reason `edgeBite` is: the bake
 * needs a 2D context, node has none, and a check that greps the source for
 * the code is not a check. With this, how much two rocks differ is a number.
 *
 * TWO EDGES, NOT ONE MIRRORED. Each row's left and right half-widths are
 * drawn from independent noise and the whole silhouette leans by `v`, so a
 * rock is not symmetrical about its own centre — which, together with the
 * variant families in `PROFILE`, is what stops a boulder field reading as one
 * rock at several sizes.
 */
export function blobSpans(cx, cy, r, squash, lumpy, seed, kind = 'broadleaf', v = 0) {
  const shape = PROFILE[kind] || PROFILE.broadleaf;
  const ry = Math.max(2, Math.round(r * squash));
  const lean = (v - 0.5) * 0.5;
  const out = [];
  for (let dy = -ry; dy <= ry; dy++) {
    const t = (dy + ry) / (2 * ry);           // 0 at the top, 1 at the foot
    const k = shape(t, v);
    if (k <= 0) continue;
    const wl = 1 + (px(seed, dy, 0) - 0.5) * 2 * lumpy;
    const wr = 1 + (px(seed + 61, dy, 0) - 0.5) * 2 * lumpy;
    const hl = Math.round(r * k * wl);
    const hr = Math.round(r * k * wr);
    if (hl + hr <= 0) continue;
    const off = Math.round(lean * r * (t - 0.5) * 2);
    out.push([Math.round(cx) + off - hl, Math.round(cy) + dy, hl + hr + 1]);
  }
  return out;
}

/** How deep the drawn edge is cut back at column `i` of a tile whose outward
 *  face is `side`. Exported so the raggedness of the mass boundary can be
 *  measured without a canvas — the bake needs a 2D context and node has none,
 *  and a check that greps for the code is not a check. */
export function edgeBite(seed, side, wx, wy, i) {
  if (side === 'up') return Math.floor(px(seed + 131, wx + i, wy) * 3);
  if (side === 'down') return Math.floor(px(seed + 137, wx + i, wy + TS) * 3);
  // ...AND THE SIDES, WHICH THIS USED TO ANSWER 0 FOR. The vertical faces
  // were cut by an inline rule that dropped a whole 16px COLUMN at a time —
  // so a vertical edge stayed perfectly straight for a tile's full height,
  // and 53.6% of the long vertical runs left in the picture sat exactly on
  // `x % 16`. The check only ever asked for 'down', so "100% of faces vary"
  // described a quarter of the geometry. Per ROW, like the others.
  if (side === 'left') return Math.floor(px(seed + 141, wx, wy + i) * 3);
  if (side === 'right') return Math.floor(px(seed + 149, wx + TS, wy + i) * 3);
  return 0;
}

/** Bake the whole canopy for one screen. Returns a canvas the size of the
 *  playfield, or null where there is no canvas to draw on (headless). */
/** The canopy in one go, for `test/_perf.ts` and the probes. The game's own bake steps it -- see `bakeCanopySteps`. */
export function bakeCanopy(tiles, biome, cols, rows, seed, ox = 0, oy = 0, pad = 0, biomeAt: any = null) {
  const it = bakeCanopySteps(tiles, biome, cols, rows, seed, ox, oy, pad, biomeAt);
  let r = it.next();
  while (!r.done) r = it.next();
  return r.value;
}

export function* bakeCanopySteps(tiles, biome, cols, rows, seed, ox = 0, oy = 0, pad = 0, biomeAt: any = null) {
  if (typeof document === 'undefined') return null;
  /* `tiles` is the PADDED grid when `pad` is set: (cols + 2*pad) by
   * (rows + 2*pad), holding the real tiles of the neighbouring screens, and
   * the canvas is the same size. `cols`/`rows` stay the playfield's, because
   * they are what the world origin is computed from. */
  const pcols = cols + pad * 2, prows = rows + pad * 2;
  const opx = ox * cols * TS - pad * TS, opy = oy * rows * TS - pad * TS;
  const c = document.createElement('canvas');
  c.width = pcols * TS; c.height = prows * TS;
  const g = c.getContext('2d');
  if (!g) return null;
  // the same list the plan uses, from the same place — see `specsFor`
  const specs = specsFor(tiles, biome, cols, rows, ox, oy, pad, biomeAt);
  // THE BAKE FROZE THE WOOD. `T.TREE` and `T.ROCK` both carry animation
  // frames, and an opaque static bake painted over every one — so on a screen
  // with nothing alive on it an examiner measured 35-55% of the picture
  // provably unable to move and only 2.3-4.1% of pixels ever changing.
  //
  // Re-baking four frames would cost four canvases a screen. Instead the bake
  // keeps the shadow and the body, and the LIT CAP of each foliage crown is
  // drawn live in `drawCanopy`, shifted by the wind. A whole arc of pixels
  // travels, which is what reads as a wood moving; a first attempt blinked
  // three single pixels per crown and measured 2.8%, i.e. nothing.
  let drew = 0;
  const alive = [];
  for (const spec of specs) {
    // A rock mass is one formation, painted from its own shape; foliage is
    // many crowns. See `paintMassSteps`.
    if (spec.kind === 'boulder') {
      drew += yield* paintMassSteps(g, tiles, pcols, prows, seed, spec, opx, opy,
        tones(BIOMES[biome] || BIOMES.heath, spec, screenDrift(seed, opx / TS, opy / TS)));
      continue;
    }
    const crowns = crownsFor(tiles, pcols, prows, seed, spec, opx, opy);
    drew += yield* paintSteps(g, tiles, biome, pcols, prows, seed, spec, opx, opy, crowns, biomeAt);
    if (MOVES.has(spec.kind)) {
      const t = tones(BIOMES[biome] || BIOMES.heath, spec, screenDrift(seed, opx / TS, opy / TS));
      alive.push({ crowns, spec, rim: t.rim, lit: t.lit, body: t.body });
    }
  }
  if (!drew) return null;
  c.alive = alive;
  c.seed = seed;
  c.opx = opx; c.opy = opy; c.pad = pad;
  return c;
}

/** the palette a mass is drawn in — foliage or stone */
/* FOUR TONES THAT ARE ACTUALLY FOUR TONES.
 *
 * The first pass took `under` straight from `pal.tree` and `body` from a 60%
 * blend toward `pal.tree2` — but in every biome those two are a few points
 * apart (`bloom`: #31502f and #4b6d45), so the crowns came out as one flat
 * dark mass with the shapes barely legible. A canopy needs a real shadow
 * under it and a real lit cap on it or it is a silhouette, not a wood. */
/* HOW FAR ONE CROWN MAY DIFFER FROM ITS NEIGHBOUR.
 *
 * Smaller than the per-screen drift on purpose: a wood is one wood, and the
 * job here is to stop three overlapping crowns reading as a single green
 * field. It is applied on top of the screen's drift, not instead of it. */
export const CROWN = 0.19;   // 0.15 until the foliage crowns (LS-186), whose shadows and blossoms dilute the drift

function tones(pal, spec, d = 0) {
  if (spec.of === T.ROCK) {
    // stone drifts too, but half as far: rock reads as rock by being greyer
    // than everything round it, and a boulder field tinted as hard as a
    // meadow stops looking like stone.
    const h = (c) => drifted(c, d, 0.08);
    return { under: h(mixHex(pal.rock2, '#000000', 0.42)),
      body: h(pal.rock2),
      lit: h(mixHex(pal.rock2, pal.rock, 0.7)),
      rim: h(mixHex(pal.rock, '#ffffff', 0.3)),
      stem: h(mixHex(pal.rock2, '#000000', 0.3)) };
  }
  // ...and the foliage shadow is DARK, because it is now doing a second job.
  // At 0.45 it was close enough to `body` that a crown's outline vanished
  // into its neighbour and a wood stayed one flat field however big the
  // crowns got.
  // ...and the leaves drift with the grass under them, so a screen reads as
  // one place rather than as a wood standing on someone else's meadow.
  /* ALL FIVE TONES, NOT ONE OF THEM.
   *
   * `lf` was applied to `under` alone — the shadow — and `body`, `lit` and
   * `rim` were handed back at their authored value. Those three are the
   * whole visible crown: the shadow is a dark edge a couple of pixels wide.
   * So every wood in a biome was painted in exactly the same green on every
   * screen, and the per-screen drift that was supposed to vary them moved
   * only the outline. Reported from play twice as the trees not varying.
   */
  const lf = (c) => drifted(c, d);
  return { under: lf(mixHex(pal.tree, '#000000', 0.66)),
    body: lf(pal.tree),
    // THE LIT CAP IS WHERE A CROWN'S SHAPE ACTUALLY LIVES.
    //
    // For foliage the bake carries only shadow and body — both dark — and all
    // the form comes from this cap, drawn live under the wind. At 0.22 it sat
    // a few values off `body`, so a wood rendered as one flat dark field and
    // no amount of enlarging the crowns changed that: the shapes were being
    // drawn, in a colour you could not see them in.
    lit: lf(mixHex(pal.tree2, pal.tuft, 0.55)),
    rim: lf(mixHex(pal.tuft, '#ffffff', 0.3)),
    stem: lf(pal.trunk) };
}

/** Smooth value noise from the same hash `px` uses.
 *
 *  `px` is a hash on integer coordinates, so sampling it at `x/7` gives 7x7
 *  blocks of one constant — fine as a dither, useless as a field. Cutting a
 *  contour out of a blocky field does not draw a seam, it punches rectangular
 *  holes, which is exactly what the first attempt at rock joints looked like.
 *  Bilinear between cell corners, with a smoothstep so the cells do not show. */
function smooth(seed, x, y, cell) {
  const fx = x / cell, fy = y / cell;
  const x0 = Math.floor(fx), y0 = Math.floor(fy);
  const tx = fx - x0, ty = fy - y0;
  const sx = tx * tx * (3 - 2 * tx), sy = ty * ty * (3 - 2 * ty);
  const a = px(seed, x0, y0), b = px(seed, x0 + 1, y0);
  const c = px(seed, x0, y0 + 1), d = px(seed, x0 + 1, y0 + 1);
  return (a + (b - a) * sx) * (1 - sy) + (c + (d - c) * sx) * sy;
}

/**
 * A ROCK FORMATION, NOT A PILE OF ROCKS.
 *
 * Boulders used to be drawn the way foliage is: one silhouette per crown,
 * scaled and wobbled. That reads as a wood, because a wood IS many similar
 * things and the eye takes it as texture. It does not read as stone. Stone
 * gets read as OBJECTS, and objects that are all the same object at different
 * sizes look exactly like what they are — reported from play as "scaled
 * clones of the same rock", which is precisely what they were.
 *
 * So a mass of `T.ROCK` is treated as one shape:
 *
 *   1. flood the connected mass to a pixel mask
 *   2. INFLATE its boundary by world-space noise, so the outline stops being
 *      the tile grid with a bite out of it and starts being a rock's edge
 *   3. distance-transform the inside — depth from the edge IS height
 *   4. shade that height in hard bands, broken by a facet noise so the result
 *      is faceted stone rather than a smooth pillow
 *
 * There is no repeated silhouette anywhere in that, because there is no
 * silhouette: the shape is whatever the terrain happens to be. It also pays
 * for the lighting twice over — the height field here is the same one
 * `light.js` turns into normals, so the relief and the picture agree by
 * construction rather than by two approximations being kept in step.
 */
/* The rock mass, a few rows at a time (2026-09-06): `smoothLabels` below is 15.3 ms of a screen's bake on the padded
 * grid, which is one step no pacing can fit in a frame. `paintMass` under it drains this for any other caller. */
function* paintMassSteps(g, tiles, cols, rows, seed, spec, opx, opy, t) {
  const W = cols * TS, H = rows * TS;
  /* THE MASS IS A FIELD, NOT A ROW OF DISCS. A disc per cell made a wood's
   * edge a scallop whose tops all sat on one cell line -- a hedge with a
   * ruled top along every screen join and every straight rank of trees. The
   * cells' labels are smoothed with the same warp the ground uses, so the
   * mass's edge meanders like the meadow's (LS-218); the fray below still
   * roughens it. */
  const massPx = (globalThis as any).__noField ? null : yield* smoothLabelsSteps((tx, ty) => (tiles[ty] && tiles[ty][tx] === spec.of) ? 1 : 0, cols, rows, seed, Math.round(opx / TS), Math.round(opy / TS), null, 1);
  const mask = new Uint8Array(W * H);
  let any = 0;
  if (massPx) { for (let i = 0; i < W * H; i++) if (massPx[i]) { mask[i] = 1; any++; } }
  else {   // the mosaic's discs, kept so the water suite's census can compare the field against the old look
    for (let ty = 0; ty < rows; ty++) {
      for (let tx = 0; tx < cols; tx++) {
        if (tiles[ty][tx] !== spec.of) continue;
        /* A ROCK TILE IS A LUMP, NOT A SQUARE (LS-199). Filling the whole tile
         * made the smallest possible formation -- one tile of rock standing in
         * a meadow -- a 16x16 SQUARE, and no amount of shading rescues that: a
         * player photographed one and asked what the random ledges were. The
         * footprint is an ellipse a little wider than the tile, so neighbours
         * still fuse into one mass (the inflate below closes what is left) and
         * a lone tile is round. */
        /* ...AND IT STAYS INSIDE ITS OWN TILE. The first cut of this let the
         * ellipse swell two pixels past the tile on every side, which looked
         * better in isolation and was a bug: the baked canopy is drawn OVER the
         * bodies (a crown must never be walked in front of), so a rock that
         * bleeds into the tile below covers whatever is standing there.
         * Reported from play in one line -- "some assets are being covered up
         * by something that should be below it". Inscribed in the tile, the
         * neighbours still touch at their edges and the inflate below fuses
         * them into one mass. */
        const cx2 = tx * TS + TS / 2, cy2 = ty * TS + TS / 2;
        const rx2 = TS * 0.5, ry2 = TS * 0.5;
        for (let py = 0; py < TS; py++) {
          for (let px2 = 0; px2 < TS; px2++) {
            const gx = tx * TS + px2, gy = ty * TS + py;
            if (gx < 0 || gy < 0 || gx >= W || gy >= H) continue;
            const u2 = (gx + 0.5 - cx2) / rx2, v2 = (gy + 0.5 - cy2) / ry2;
            if (u2 * u2 + v2 * v2 > 1) continue;
            mask[gy * W + gx] = 1; any++;
          }
        }
      }
    }
  }
  if (!any) return 0;
  // 2. INFLATE. Each empty pixel next to the mass joins it when the noise at
  // its own WORLD position says so — world position, so the swell continues
  // across a screen border instead of stopping dead at it.
  const grown = mask.slice();
  for (let y = 0; y < H; y++) {
    for (let x = 0; x < W; x++) {
      if (mask[y * W + x]) continue;
      let touch = 0;
      if (x > 0 && mask[y * W + x - 1]) touch++;
      if (x < W - 1 && mask[y * W + x + 1]) touch++;
      if (y > 0 && mask[(y - 1) * W + x]) touch++;
      if (y < H - 1 && mask[(y + 1) * W + x]) touch++;
      if (!touch) continue;
      const n = px(seed + 211, x + opx, y + opy);
      if (n < 0.34 + touch * 0.12) grown[y * W + x] = 1;
    }
  }
  yield;   /* a pass a step (LS-480): the canopy's one step was 420 ms at a phone's pace */
  // ...and bite back, with different noise, so the edge is not a uniform swell
  for (let y = 0; y < H; y++) {
    for (let x = 0; x < W; x++) {
      if (!grown[y * W + x]) continue;
      const edge = (x === 0 || !grown[y * W + x - 1]) || (x === W - 1 || !grown[y * W + x + 1])
        || (y === 0 || !grown[(y - 1) * W + x]) || (y === H - 1 || !grown[(y + 1) * W + x]);
      if (!edge) continue;
      if (px(seed + 223, x + opx, y + opy) < 0.22) grown[y * W + x] = 0;
    }
  }
  yield;
  // 3. depth from the edge. Closed edges: the formation continues onto the
  // next screen and must not thin out at the seam.
  const depth = distanceField(grown, W, H, false);
  yield;
  // 4. shade it. Hard bands, and a facet noise that shifts which band a pixel
  // lands in — without it the bands are concentric rings and the formation
  // reads as a pillow.
  // READ FIRST, THEN WRITE BACK. `putImageData` replaces the whole rectangle
  // it is given rather than compositing into it, so building a fresh
  // ImageData and putting it down would erase every layer already drawn —
  // which for a non-rock biome is the entire wood, since the rocks are the
  // second spec. Start from what is on the canvas and touch only the mass.
  const img = g.getImageData && g.getImageData(0, 0, W, H);
  if (!img || !img.data) return 0;
  const d = img.data;
  const rgb = (hex) => [parseInt(hex.slice(1, 3), 16), parseInt(hex.slice(3, 5), 16),
    parseInt(hex.slice(5, 7), 16)];
  const band = [rgb(t.under), rgb(t.body), rgb(t.lit), rgb(t.rim)];
  for (let y = 0; y < H; y++) {
    if (y && (y & 31) === 0) yield;   // the colour pass in bands of 32 rows (the canvas is this bake's own; nothing draws on it meanwhile)
    for (let x = 0; x < W; x++) {
      const i = y * W + x;
      if (!grown[i]) continue;
      // ABSOLUTE DEPTH, NOT DEPTH RELATIVE TO THE BIGGEST THING ON SCREEN.
      //
      // Normalising by the screen's deepest point made a rock's tone depend
      // on what ELSE was on the screen: a lone boulder two pixels thick, next
      // to a formation twenty deep, scored 0.1 and came out entirely in the
      // darkest band — a flat black rectangle. Seen immediately on the first
      // render. Depth in PIXELS is a property of the rock itself, so a small
      // rock is simply a rock that is all edge, which is what a small rock is.
      // Two scales of break-up: broad patches that make one part of the mass
      // sit higher than another, and a light per-pixel dither so the band
      // boundaries are not clean curves. The broad term is quantised to 5px
      // cells because at 1px it is dither too and the whole thing reads as
      // television static.
      const facet = (smooth(seed + 307, x + opx, y + opy, 6) - 0.5) * 3.4
        + (px(seed + 311, x + opx, y + opy) - 0.5) * 0.45;
      const dpt = depth[i] + facet;
      /* THE MASS IS LIT BY ITS OWN SURFACE (LS-199), NOT BY HOW FAR IN THE
       * PIXEL IS. Banding on depth alone draws CONCENTRIC RINGS: on a large
       * formation the facet noise hides them, but on a single tile of rock --
       * a lone boulder in a field -- it comes out as a square panel with a
       * hook carved in it, which is what a player photographed and asked
       * about ("what are these random ledges..."). Depth IS height here, so
       * the mass has a real surface: take its slope, point a normal up out of
       * it, and light that from the same quarter every other solid in the
       * game is lit from. The bands stay the same three tones, and the seams
       * and the mica fleck below are untouched -- what changes is WHICH band
       * a pixel lands in, and now it depends on which way the rock faces. */
      const hOf = (xx: number, yy: number) => {
        if (xx < 0 || yy < 0 || xx >= W || yy >= H) return 0;
        const k2 = yy * W + xx;
        return grown[k2] ? Math.sqrt(Math.max(0, depth[k2])) * 1.7 : 0;
      };
      const hx = hOf(x + 1, y) - hOf(x - 1, y), hy = hOf(x, y + 1) - hOf(x, y - 1);
      // the surface normal in screen space (y down), then the sun from up and
      // to the left, a little in front
      const nx = -hx, ny = -hy, nz = 2.2;
      const nl = Math.hypot(nx, ny, nz) || 1;
      const lam = (nx * -0.42 + ny * -0.66 + nz * 0.62) / nl;
      // THREE TONES OF STONE, AND A HIGHLIGHT THAT IS A FLECK.
      //
      // `rim` is a highlight colour — rock mixed 30% towards white. Letting
      // the deepest band fill with it turned the middle of every large
      // formation into a white blank, which is worse than the flat black it
      // replaced. The interior tops out at `lit`, and `rim` is scattered
      // sparsely well inside the edge, where a mica glint would catch.
      // the foot of the mass stays dark whatever it faces: that is the ground
      // contact, and it is what stops the rock reading as a hole
      let k = dpt < 1.3 ? 0 : lam > 0.80 ? 2 : lam > 0.62 ? 1 : dpt < 3 ? 0 : 1;
      // JOINTS. A scree mass is boulders fused together, and what says so is
      // the dark seams between them, not the speckle on top. A low-frequency
      // field cut at its own contour lines draws those seams across the whole
      // formation, so the mass has parts instead of being one grey field.
      const j = smooth(seed + 349, x + opx, y + opy, 9) * 3.2
        + smooth(seed + 353, x + opx, y + opy, 4) * 0.45;
      // ...and ONLY on a mass big enough to have parts. On a lone boulder the
      // seam is a dark arc scribbled across it -- the "hook" a player saw
      // carved into what should be a rock.
      if (dpt > 4 && Math.abs((j % 1) - 0.5) < 0.045) k = 0;
      else if (dpt > 5 && px(seed + 331, x + opx, y + opy) < 0.018) k = 3;
      const c = band[k];
      const o = i * 4;
      d[o] = c[0]; d[o + 1] = c[1]; d[o + 2] = c[2]; d[o + 3] = 255;
    }
  }
  /* A MASS STANDS ON THE GROUND; IT IS NOT A HOLE IN IT.
   *
   * Everything above shades the rock by its distance from its own edge, which
   * gives it an interior. What it does not give it is a PLACE: the formation
   * met the ground along a uniform dark keyline on all four sides, with no lit
   * top and nothing cast below, and an art critic reading a render of the whole
   * island put it first of seventeen defects at 0.9 confidence -- the blob
   * "reads as a hole punched in the sand" while the cliff beside it, which has
   * both, reads as rock.
   *
   * Two passes, and both are the same devices `drawCliffs` already uses on a
   * cliff face, which is why they agree with it:
   *
   *   the LIT BRINK   the top edge catches the sky, so the mass has a surface
   *                   that faces up rather than an outline
   *   the CAST SHADOW the ground below it is darkened, and that is what says
   *                   the rock is ON the ground rather than through it
   *
   * Both are drawn in fixed light/dark rather than palette tones, so they cost
   * a country nothing and read the same in all six -- which is the point, since
   * the ground's own brightness is already spoken for by the terrace shading
   * and cannot carry depth as well. */
  /* A MASS STANDS ON THE GROUND; IT IS NOT A HOLE IN IT.
   *
   * Everything above shades the rock by its distance from its own edge, which
   * gives it an interior. What it did not give it was a PLACE: the formation
   * met the ground along one uniform dark keyline on every side, with nothing
   * facing up. An art critic reading a render of the whole island put that
   * first of seventeen defects at 0.9 confidence -- the blob "reads as a hole
   * punched in the sand" while the cliff beside it, which has a lit brink,
   * reads as rock.
   *
   * So the top edge catches the sky, one pixel, the same device `drawCliffs`
   * uses on a cliff face -- which is why the two agree. It is drawn in a fixed
   * light rather than a palette tone, so it costs a country nothing and reads
   * the same in all six; the ground's own brightness is already spoken for by
   * the terrace shading and cannot carry depth as well.
   *
   * A CAST SHADOW ON THE GROUND BELOW WAS TRIED AND IS NOT HERE. The other
   * half of the cliff's trick is to darken the ground under its southern edge,
   * and on a grown mass it does not land: the bake is composited over the tile
   * layer and is transparent off the formation, and writing semi-transparent
   * dark pixels into that margin changed the rendered frame by a measured zero
   * -- the layer is clipped to the mass. It is left out rather than shipped
   * unproven; the check beside this one would have passed either way, which is
   * how the first attempt at it was caught. */
  const LIFT = [255, 250, 236];
  for (let y = 1; y < H; y++) {
    for (let x = 0; x < W; x++) {
      const i = y * W + x;
      if (!grown[i] || grown[(y - 1) * W + x]) continue;
      const o = i * 4;
      for (let c2 = 0; c2 < 3; c2++) d[o + c2] = Math.round(d[o + c2] * 0.45 + LIFT[c2] * 0.55);
    }
  }
  g.putImageData(img, 0, 0);
  return any;
}

function* paintSteps(g, tiles, biome, cols, rows, seed, spec, opx, opy, given = null,
  biomeAt: any = null) {
  /* A TREE IS THE COLOUR OF THE GROUND IT STANDS IN.
   *
   * The whole wood took one palette, the screen's, while the ground beneath it
   * had already moved to a per-tile biome — so a wood crossing a biome
   * boundary changed colour where the SCREEN changed rather than where the
   * country did, and a single stand of trees came out two colours with the
   * join on a straight line. Reported from the map as the trees changing
   * colour midway.
   *
   * The SHAPE still comes from the screen's majority biome, because a shape is
   * what a crown is and a crown cannot be half broadleaf and half snag. Only
   * the tones move, and they move per crown, from the crown's own world tile
   * — including the warm/cool drift, which is a world field now too. */
  const tonesCache = new Map();
  const tonesFor = (wtx, wty) => {
    const name = biomeAt ? biomeAt(wtx, wty) : biome;
    const d = screenDrift(seed, wtx, wty);
    // buckets, so a slow field does not mean a palette per pixel
    const b = Math.round(d * 6);
    const k = name + '|' + b;
    let v = tonesCache.get(k);
    if (!v) { v = tones(BIOMES[name] || BIOMES.heath, spec, b / 6); tonesCache.set(k, v); }
    return v;
  };
  const pal = BIOMES[biome] || BIOMES.heath;
  const t = tonesFor(opx / TS + cols / 2, opy / TS + rows / 2);
  // 1. NOTHING SHOWS THROUGH. Every solid tile of this mass is filled first,
  // so wherever the shapes land the region is covered — the crowns decide the
  // silhouette, not whether there are holes in the wall.
  /* ...AND THE EDGE OF THAT FILL IS NOT THE TILE GRID.
   *
   * Every solid tile was filled as a flat 16x16 square, so wherever a crown
   * did not happen to spill over the boundary the mass ended on a ruled
   * lattice line: measured, 58.1% of boundary faces ran straight on into the
   * next tile's face, which is why the scree and ash walls still showed hard
   * right angles. The crowns broke the silhouette outward and nothing broke
   * it inward.
   *
   * An outward-facing edge is now cut back by a pixel or three, per column,
   * from the same world-space hash the crowns use. The tile stays solid —
   * collision reads the grid and nothing here can move a wall — it simply
   * stops being drawn all the way to its own corner.
   */
  let n = 0;
  g.fillStyle = t.under;   // replaced per tile below where the fill runs
  // THE SCREEN EDGE IS A FACE, NOT AN INTERIOR.
  //
  // This answered TRUE off-screen — "the mass continues onto the next screen"
  // — so a tile in row 0 had `open.up` false, took no edge bite, and its fill
  // ran flat to y=0. The result was a slab of the darkest tone lying along the
  // top of the screen wherever trees reached it, and clamping the crowns to
  // fit inside the canvas made it worse rather than better: the crowns moved
  // down and stopped covering the slab they had been hiding. Reported twice
  // from play as the tops of the trees being cut off.
  //
  // Off-screen is treated as open, so the boundary face is bitten like every
  // other face and the wood ENDS in foliage instead of in a ruled line. The
  // cost is that the bite on one screen's top does not line up with the bite
  // on the next screen's bottom during a vertical transition; that is a few
  // pixels for a few frames, against a flat black bar that was there the whole
  // time you stood still.
  const isMassAt = (x, y) => (x < 0 || y < 0 || x >= cols || y >= rows
    ? false : tiles[y][x] === spec.of);
  for (let y = 0; y < rows; y++) {
    for (let x = 0; x < cols; x++) {
      if (tiles[y][x] !== spec.of) continue;
      n++;
      const wx = opx + x * TS, wy = opy + y * TS;
      const open = {
        up: !isMassAt(x, y - 1), down: !isMassAt(x, y + 1),
        left: !isMassAt(x - 1, y), right: !isMassAt(x + 1, y),
      };
      if (!open.up && !open.down && !open.left && !open.right) {
        g.fillRect(x * TS, y * TS, TS, TS);
        continue;
      }
      // column by column, so the bite varies along the face instead of
      // stepping once per tile
      // COLUMNS for the horizontal faces, ROWS for the vertical ones — a
      // face is only ragged along its own length.
      for (let i = 0; i < TS; i++) {
        let top = 0, bot = TS;
        if (open.up) top += edgeBite(seed, 'up', wx, wy, i);
        if (open.down) bot -= edgeBite(seed, 'down', wx, wy, i);
        if (bot > top) g.fillRect(x * TS + i, y * TS + top, 1, bot - top);
      }
      for (let i = 0; i < TS; i++) {
        const l = open.left ? edgeBite(seed, 'left', wx, wy, i) : 0;
        const r = open.right ? edgeBite(seed, 'right', wx, wy, i) : 0;
        if (l > 0) g.clearRect(x * TS, y * TS + i, l, 1);
        if (r > 0) g.clearRect(x * TS + TS - r, y * TS + i, r, 1);
      }
    }
  }
  if (!n) return 0;
  yield;
  const crowns = given || crownsFor(tiles, cols, rows, seed, spec, opx, opy);
  const squash = spec.squash || 1;
  // 2. THE TRUNKS, under everything, where the mass ends and you could see
  // beneath it. Drawn first so a crown in front covers its neighbour's stem.
  if (spec.trunk > 0) {
    g.fillStyle = t.stem;
    /* EVERY CROWN THROWS A SHADOW ON THE GROUND UNDER IT.
   *
   * In a top-down view the ground shadow is the ONLY thing that says an object
   * stands above the floor — without it a canopy is a sticker lying flat on
   * the sand. Measured across the island, nothing but the player cast one, so
   * the whole world had no vertical axis: trees, signs and rocks all sat in
   * the same plane as the tiles they were drawn on.
   *
   * Drawn before the crowns and offset down and right, matching the light the
   * lit caps already use, so a crown in front covers the shadow of the one
   * behind it and the mass still reads as many trees rather than one field.
   * It goes into the BAKE, which is transparent where there is no foliage, so
   * it lands on the ground rather than on a rectangle. */
  g.fillStyle = 'rgba(16,12,24,0.30)';
  let shadowCount = 0;
  for (const c of crowns) {
    const rr = Math.max(3, Math.round(c.r * 0.86));
    const sq = (spec.squash || 1);
    blob(g, c.x + 2, c.y + Math.round(c.r * sq * 0.72) + 2, rr, 0.42,
      spec.lumpy * 0.5, seed + 911 + c.x * 7 + c.y, spec.kind, c.v);
    if (++shadowCount % 32 === 0) yield;
  }

  for (const c of crowns) {
      const ty = Math.floor(c.y / TS), tx = Math.floor(c.x / TS);
      const below = ty + 1 < rows ? tiles[ty + 1][tx] : spec.of;
      if (below === spec.of) continue;              // nothing to see under it
      const top = c.y + Math.round(c.r * squash) - 1;
      // LS-186: a trunk with a shaded side, bark and a root flare
      paintTrunk(g, c.x, top, spec.trunk + 2, seed + c.x * 3 + c.y, { ...t, dark: t.under }, 3);
      g.fillStyle = t.stem;
    }
  }
  // 3. THE CROWNS. Shadow, body, lit cap, and a rim of bright pixels on the
  // side the light comes from — four passes, not one, or a canopy is a blob.
  yield;
  let crownCount = 0;
  for (const c of crowns) {
    // This crown's own greens. Computed here, once per screen bake, and the
    // live ones cached onto the crown so `drawCanopy` costs nothing per frame.
    const ct = c.tint || 0;
    const ct2 = tonesFor(Math.floor((opx + c.x) / TS), Math.floor((opy + c.y) / TS));
    const cUnder = drifted(ct2.under, ct, CROWN);
    const cBody = drifted(ct2.body, ct, CROWN);
    c.bodyC = cBody;
    // the live pass is the lit caps and the rim pixels; both carry more of
    // the screen's drift than the body, or the wind-drawn wood reads as one green
    c.litC = drifted(ct2.lit, ct, CROWN);
    c.rimC = drifted(ct2.rim, ct, CROWN);
    // A DARK EDGE ON EVERY CROWN, not just a shadow under it.
    //
    // The shadow used to be the same radius as the body and one pixel lower,
    // so only a sliver showed along the bottom. With crowns overlapping three
    // deep, that made a wood one continuous green field with no tree in it —
    // and enlarging the crowns made it worse, because bigger crowns at the
    // same spacing merge more completely. Drawing the shadow at full radius
    // and the body two pixels inside it gives every crown its own outline, so
    // the ones in front read in front and the mass reads as many trees.
    /* LS-186: FOLIAGE, NOT A BLOB. The crown is leaf clusters with a lit cap
     * and a shadow each; the still part is baked here, the lit part is
     * redrawn with the wind in `drawCanopy` for the kinds that move. */
    const name = biomeAt ? biomeAt(Math.floor((opx + c.x) / TS), Math.floor((opy + c.y) / TS)) : biome;
    c.flora = spec.kind === 'snag' ? 'snag' : spec.kind === 'scrub' ? 'scrub' : name === 'fen' ? 'willow' : 'broadleaf';
    c.blossom = name === 'bloom' ? drifted('#f2c0d6', ct, CROWN) : null;   // blossoms drift with their screen too
    c.stemC = ct2.stem;
    // the leaf shadow carries the screen's full drift, as the under tone does,
    // or the wood's colour swings less from screen to screen than promised
    c.darkC = mixHex(cUnder, cBody, 0.35);
    const ft = { under: cUnder, body: cBody, lit: c.litC, rim: c.rimC, stem: ct2.stem, dark: c.darkC };
    paintCrownBody(g, c.x, c.y - c.lift, c.r, squash, seed + c.x * 7 + c.y, c.flora, ft, c.blossom);
    if (!MOVES.has(spec.kind)) paintCrownLit(g, c.x, c.y - c.lift, c.r, squash, seed + c.x * 7 + c.y, c.flora, ft, 0);
    if (++crownCount % 32 === 0) yield;
  }
  return n;
}
/** Draw the baked canopy. It goes over the ground and under the bodies. */
/**
 * @param frontOnly  redraw only the crowns, over bodies that are standing
 *                   under them. The baked mass is NOT painted again: it
 *                   covers solid tiles nobody can stand on, so a second pass
 *                   of it could only hide something that should be visible.
 */
/* THE LIT CROWNS PACK INTO THE ATLAS (the robustness lens, round 50). `atlas.ts`'s own header names the three things
 * that made 13,195 canvases in two minutes and cost a phone its WebGL context: "one per creature sprite, one per
 * body's normal map, ONE PER LIT CROWN". The first two moved into `atlasCell` and registered forgetters; this one
 * never did. Measured, walking a whole world twelve frames a screen: 34 canvases after one screen, 1,064 after nine,
 * 7,852 after ninety -- and walking back over the first nine made 1,345 more, because crossing 3,000 entries CLEARED
 * the whole map including the screen under the player. The key carried neither the world's seed nor the crown's
 * tones, so 20 of 9,560 keys served two different pictures across eight worlds -- a crown in a new world at the same
 * place got the old world's colours. */
const LIT_CROWNS = new Map<string, { cv: any; ox: number; oy: number; sx: number; sy: number; w: number; h: number }>();
onAtlasReset(() => LIT_CROWNS.clear());
/** How many crowns are cached, and on how many DISTINCT canvases they sit. Exported so a check can ask whether the
 *  cache is packing into the shared atlas or making a texture per crown (the robustness lens, round 50). */
export function crownCacheStats() {
  const cvs = new Set<any>();
  for (const sp of LIT_CROWNS.values()) cvs.add(sp.cv);
  return { crowns: LIT_CROWNS.size, canvases: cvs.size };
}
export function drawCanopy(g, baked, frame = 0, frontOnly = false) {
  if (!baked) return;
  // the bake carries a margin of the neighbouring screens' wood, so it hangs
  // outside the playfield by `pad` tiles on every side
  const P = (baked.pad || 0) * TS;
  g.save();
  /* CLIPPED TO ITS OWN PLAYFIELD.
   *
   * The bake overhangs by `pad` tiles so no crown has to be resized to fit the
   * canvas, but that overhang lands on the neighbouring screen, which paints
   * the very same crowns itself from the same world lattice. Letting both draw
   * it means every seam is painted twice with alpha, so a screen paints only
   * its own tiles; because both sides compute the identical crown from world
   * coordinates, the halves meet without a join.
   *
   * NOT WORTH A SEAM NUMBER, and I claimed one here before checking. The
   * column statistic sat at 10.1-17.2 with the overdraw and 10.1-16.6 without
   * it, so whatever is left on the vertical seams is something else; this is
   * kept because drawing a neighbour's wood is wrong, not because it measured
   * as the fix. */
  if (P) {
    g.beginPath();
    g.rect(0, 0, baked.width - P * 2, baked.height - P * 2);
    g.clip();
  }
  g.translate(-P, -P);
  if (!frontOnly) g.drawImage(baked, 0, 0);
  /* THE WOOD MOVES IN THE WIND.
   *
   * The bake carries the shadow and the body; a foliage crown's LIT CAP is
   * drawn here instead, leaning with the same wind field the grass reads. An
   * opaque static bake had covered `T.TREE`'s own animation across 35-55% of
   * a wild screen — measured on a screen with nothing alive on it, the tiles
   * alone moved 8.9% of the playfield and the bake left 3.5%.
   *
   * A first attempt blinked three single pixels per crown and recovered
   * nothing. What reads as a wood breathing is a whole ARC of pixels
   * travelling, so: two caps, counter-phased, one leaning with the wind and a
   * dimmer one against it, which walks the light across each crown rather
   * than sliding the crown sideways.
   */
  /* THE LIVE CROWNS ARE SPRITES (performance, Vijay: "performance is starting
   * to lag on mobile on new build when walking around"). Every crown that
   * sways was painted again each frame, disc by disc: thirty-three thousand
   * fillRects a frame with four screens in view, the largest single cost of
   * a frame by far. The sway is a whole number from minus three to three, so
   * a crown has at most seven pictures; each is painted once, into a small
   * canvas kept on the bake, and drawn from there. */
  // one cache for every screen's crowns (LS-235): a cache on the bake died with every re-bake, and its pictures were painted again
  const cache = LIT_CROWNS;
  const canSprite = typeof document !== 'undefined';
  const pending = [];
  const flush = () => {
    for (const { sp, x, y } of pending) g.drawImage(sp.cv, sp.sx, sp.sy, sp.w, sp.h, x, y, sp.w, sp.h);
    pending.length = 0;
  };
  for (const layer of baked.alive || []) {
    const squash = layer.spec.squash || 1;
    for (const c of layer.crowns) {
      const pad = 8, ox = pad + Math.ceil(c.r * 1.3) + 3, oy = pad + Math.ceil(c.r) + 2;
      const wpx = ox * 2, hpx = oy + Math.ceil(c.r * squash) + Math.ceil(c.r * 2.2) + pad;
      // Padded neighbours need no sprite when its entire atlas cell is outside our clip.
      if (canSprite && P && (c.x + ox <= P || c.x - ox >= baked.width - P
        || c.y - c.lift - oy + hpx <= P || c.y - c.lift - oy >= baked.height - P)) continue;
      const w = windAt((baked.opx || 0) + c.x, frame);
      const sway = Math.round((w - 0.5) * 4);   // five pictures a crown, not seven
      const tones = { under: '', body: c.bodyC || layer.body, lit: c.litC || layer.lit, rim: c.rimC || layer.rim, stem: c.stemC || '', dark: c.darkC || '' };
      if (!canSprite) {
        paintCrownLit(g, c.x, c.y - c.lift, c.r, squash, baked.seed + c.x * 7 + c.y, c.flora || 'broadleaf', tones, sway, c.blossom || null);
        continue;
      }
      // ...and the key carries the WORLD it was painted for, and the radius and tones that decide the picture
      const key = baked.seed + '|' + (baked.opx || 0) + ',' + (baked.opy || 0) + ',' + c.x + ',' + c.y + ',' + sway + ','
        + (c.flora || '') + ',' + (c.blossom ? 1 : 0) + ',' + Math.round(c.r) + ',' + tones.body + tones.lit;
      let sp = cache.get(key);
      if (!sp) {
        const cell = atlasCell(wpx, hpx, flush);
        const cg = cell.g;
        if (!cg) { flush(); paintCrownLit(g, c.x, c.y - c.lift, c.r, squash, baked.seed + c.x * 7 + c.y, c.flora || 'broadleaf', tones, sway, c.blossom || null); continue; }
        /* A CELL IS SHARED, so clip willow fronds to their own slot. */
        cg.save();
        cg.beginPath(); cg.rect(cell.sx, cell.sy, wpx, hpx); cg.clip();
        cg.clearRect(cell.sx, cell.sy, wpx, hpx);
        paintCrownLit(cg, cell.sx + ox, cell.sy + oy, c.r, squash, baked.seed + c.x * 7 + c.y, c.flora || 'broadleaf', tones, sway, c.blossom || null);
        cg.restore();
        sp = { cv: cell.canvas, ox, oy, sx: cell.sx, sy: cell.sy, w: wpx, h: hpx };
        cache.set(key, sp);
      }
      pending.push({ sp, x: c.x - sp.ox, y: c.y - c.lift - sp.oy });
    }
  }
  // Finish atlas writes before reading sprites; a reset flushes before slots are reused.
  flush();
  g.restore();
}
